import { useCallback, useEffect, useRef, useState } from 'react';
import { getSupabaseClient } from '../../../data/supabase-client';
import { selectReportFile, type ReportFileHandle } from '../../../platform/report-file';
import { SAVED_REPORTS_BUCKET } from '../../../data/writers/save-generated-report';

/**
 * VIEWING AND RE-SHARING A SAVED REPORT — M10 / S10-05.
 *
 * **THE SAVED OBJECT IS THE DOCUMENT. NOTHING HERE RENDERS ANYTHING.** This
 * module holds no report generator, no content model and no live query: it
 * signs a short-lived URL, downloads the exact stored bytes, writes them to the
 * device's temporary cache, and hands the handle to the same
 * `ReportFilePort` S10-04 already uses for View and Share.
 *
 * **A MISSING OBJECT IS AN EXPLICIT FAILURE.** It is never quietly rebuilt from
 * today's Captures — that would hand a client a document that was never sent.
 *
 * **THE CACHE COPY IS DISPOSABLE, THE REMOTE OBJECT IS THE TRUTH.** The local
 * file exists so Android's viewer and share sheet have something to open; it
 * carries no identity, and S10-04's existing sweep removes it in time.
 */

export type SavedReportFileStatus = 'idle' | 'working';

export interface SavedReportFileState {
  readonly status: SavedReportFileStatus;
  readonly message: string | null;
  view(): Promise<void>;
  share(): Promise<void>;
}

/** Long enough to download a few megabytes on a poor connection, no longer. */
const SIGNED_URL_TTL_SECONDS = 300;

const MISSING =
  'This saved report could not be opened — its file could not be found. Nothing has been changed.';
const DOWNLOAD_FAILED = 'The saved report could not be downloaded. Check your connection.';

export function useSavedReportFile(input: {
  readonly reportId: string;
  readonly ownerUserId: string;
  readonly projectId: string;
  readonly filename: string;
}): SavedReportFileState {
  const [files] = useState(() => selectReportFile());
  const [status, setStatus] = useState<SavedReportFileStatus>('idle');
  const [message, setMessage] = useState<string | null>(null);
  const handleRef = useRef<ReportFileHandle | null>(null);
  const busyRef = useRef(false);

  /** The temporary copy belongs to this screen and leaves with it. */
  useEffect(() => {
    return () => {
      const handle = handleRef.current;
      handleRef.current = null;
      if (handle) void files.discard(handle);
    };
  }, [files]);

  /**
   * Fetch the saved bytes and put them somewhere the device can open.
   *
   * The signed URL is minted HERE, at the moment of use, and never stored —
   * not in the row, not in the snapshot, not in state.
   */
  const materialise = useCallback(async (): Promise<ReportFileHandle | null> => {
    if (handleRef.current) return handleRef.current;

    const client = getSupabaseClient();
    if (!client) {
      setMessage(DOWNLOAD_FAILED);
      return null;
    }

    const path = `${input.ownerUserId}/${input.projectId}/${input.reportId}.pdf`;
    const signed = await client.storage
      .from(SAVED_REPORTS_BUCKET)
      .createSignedUrl(path, SIGNED_URL_TTL_SECONDS);

    if (signed.error || !signed.data?.signedUrl) {
      setMessage(MISSING);
      return null;
    }

    let bytes: Uint8Array;
    try {
      const response = await fetch(signed.data.signedUrl);
      if (!response.ok) {
        setMessage(response.status === 404 ? MISSING : DOWNLOAD_FAILED);
        return null;
      }
      bytes = new Uint8Array(await response.arrayBuffer());
    } catch {
      setMessage(DOWNLOAD_FAILED);
      return null;
    }

    if (bytes.byteLength === 0) {
      setMessage(MISSING);
      return null;
    }

    try {
      handleRef.current = await files.write(input.filename, bytes);
      return handleRef.current;
    } catch {
      setMessage('This device could not open the saved report.');
      return null;
    }
  }, [files, input.filename, input.ownerUserId, input.projectId, input.reportId]);

  const run = useCallback(
    async (action: (handle: ReportFileHandle) => Promise<void>) => {
      if (busyRef.current) return;
      busyRef.current = true;
      setStatus('working');
      setMessage(null);
      try {
        const handle = await materialise();
        if (handle) await action(handle);
      } finally {
        busyRef.current = false;
        setStatus('idle');
      }
    },
    [materialise],
  );

  const view = useCallback(async () => {
    await run(async (handle) => {
      try {
        await files.view(handle);
      } catch {
        // The saved report is untouched and still shareable.
        setMessage('This device could not open the PDF. You can still share it.');
      }
    });
  }, [files, run]);

  const share = useCallback(async () => {
    await run(async (handle) => {
      try {
        // A cancelled share is not a failure — S10-04's rule, unchanged.
        await files.share(handle, { title: input.filename });
      } catch {
        setMessage('Sharing did not start. The saved report is unchanged.');
      }
    });
  }, [files, input.filename, run]);

  return { status, message, view, share };
}
